import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Section,
  Cell,
  Input,
  Button,
  Placeholder,
  Text,
  Textarea,
} from '@telegram-apps/telegram-ui';
import { useAuth } from '../hooks/useAuth';
import { ContactManagerLink } from '../components/ContactManagerLink';
import type { PhoneCheckResult, CompanyInfo } from '../types';

type AuthStep = 'phone' | 'select-company' | 'confirm' | 'not-found' | 'request-sent';

export function AuthPage() {
  const navigate = useNavigate();
  const { checkPhone, login, requestAccess } = useAuth();

  const [step, setStep] = useState<AuthStep>('phone');
  const [phone, setPhone] = useState('+7');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneResult, setPhoneResult] = useState<PhoneCheckResult | null>(null);
  const [, setSelectedCompany] = useState<CompanyInfo | null>(null);

  // Форма заявки (для не найденных)
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [inn, setInn] = useState('');
  const [comment, setComment] = useState('');

  /**
   * Шаг 1: Проверка телефона
   */
  async function handleCheckPhone() {
    if (phone.length < 11) {
      setError('Введите корректный номер телефона');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await checkPhone(phone);
      setPhoneResult(result);

      if (result.found && result.contactId && result.companyId) {
        // Если несколько компаний — показываем выбор
        if (result.companies && result.companies.length > 1) {
          setStep('select-company');
        } else {
          setStep('confirm');
        }
      } else {
        setStep('not-found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка проверки телефона');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Шаг 1.5: Выбор компании из списка
   */
  function handleSelectCompany(company: CompanyInfo) {
    setSelectedCompany(company);
    // Обновляем phoneResult с выбранной компанией
    if (phoneResult) {
      setPhoneResult({
        ...phoneResult,
        companyId: company.id,
        companyName: company.name,
        companyInn: company.inn,
      });
    }
    setStep('confirm');
  }

  /**
   * Шаг 2a: Подтверждение и вход
   */
  async function handleLogin() {
    if (!phoneResult?.contactId || !phoneResult?.companyId) return;

    setIsLoading(true);
    setError('');

    try {
      await login({
        contactId: phoneResult.contactId,
        companyId: phoneResult.companyId,
        phone,
        fullName: phoneResult.contactName,
        companyInn: phoneResult.companyInn,
      });

      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка входа');
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Шаг 2b: Отправка заявки
   */
  async function handleRequestAccess() {
    if (!fullName.trim()) {
      setError('Введите ваше имя');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await requestAccess({
        fullName: fullName.trim(),
        phone,
        email: email.trim() || undefined,
        inn: inn.trim() || undefined,
        comment: comment.trim() || undefined,
      });

      setStep('request-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка отправки заявки');
    } finally {
      setIsLoading(false);
    }
  }

  // ==========================================
  // Рендер по шагам
  // ==========================================

  // Шаг 1: Ввод телефона
  if (step === 'phone') {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0 8px' }}>
          <img
            src="/logo-stotek.svg"
            alt="СТОТЕК"
            style={{ height: '48px', objectFit: 'contain' }}
          />
        </div>
        <Placeholder
          header="Добро пожаловать"
          description="Введите номер телефона, на который зарегистрирована ваша компания в нашей системе"
        />

        <Section>
          <Input
            header="Номер телефона"
            placeholder="+7 (___) ___-__-__"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
          />
        </Section>

        {error && (
          <Text style={{ color: 'var(--tgui--destructive_text_color)', padding: '8px 16px', fontSize: '14px' }}>
            {error}
          </Text>
        )}

        <div style={{ padding: '16px' }}>
          <Button
            size="l"
            stretched
            onClick={handleCheckPhone}
            loading={isLoading}
            disabled={phone.length < 11}
          >
            Продолжить
          </Button>
        </div>

        <div style={{ padding: '0 16px' }}>
          <ContactManagerLink showContacts />
        </div>
      </div>
    );
  }

  // Шаг 1.5: Выбор компании (если несколько)
  if (step === 'select-company' && phoneResult?.companies) {
    return (
      <div style={{ padding: '16px' }}>
        <Placeholder
          header="Выберите компанию"
          description={`На ваш номер зарегистрировано несколько компаний. Выберите, от имени какой компании вы хотите войти.`}
        />

        <Section header={`Компании (${phoneResult.companies.length})`}>
          {phoneResult.companies.map((company) => (
            <Cell
              key={company.id}
              subtitle={company.inn ? `ИНН: ${company.inn}` : undefined}
              onClick={() => handleSelectCompany(company)}
              after={
                <span style={{ color: 'var(--stotek-red, #EC2226)', fontSize: '20px' }}>
                  ›
                </span>
              }
              style={{ cursor: 'pointer' }}
            >
              {company.name}
            </Cell>
          ))}
        </Section>

        <div style={{ padding: '16px' }}>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={() => {
              setStep('phone');
              setPhoneResult(null);
              setSelectedCompany(null);
              setError('');
            }}
          >
            Назад
          </Button>
        </div>
      </div>
    );
  }

  // Шаг 2a: Подтверждение компании
  if (step === 'confirm' && phoneResult) {
    return (
      <div style={{ padding: '16px' }}>
        <Placeholder
          header="Подтверждение"
          description="Мы нашли вашу компанию. Подтвердите, что это верно."
        />

        <Section header="Данные">
          <Cell subtitle="Контакт">
            {phoneResult.contactName || 'Не указано'}
          </Cell>
          <Cell subtitle="Компания">
            {phoneResult.companyName || 'Не указана'}
          </Cell>
          {phoneResult.companyInn && (
            <Cell subtitle="ИНН">
              {phoneResult.companyInn}
            </Cell>
          )}
          <Cell subtitle="Телефон">
            {phone}
          </Cell>
        </Section>

        {error && (
          <Text style={{ color: 'var(--tgui--destructive_text_color)', padding: '8px 16px', fontSize: '14px' }}>
            {error}
          </Text>
        )}

        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            size="l"
            stretched
            onClick={handleLogin}
            loading={isLoading}
          >
            Подтверждаю, это моя компания
          </Button>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={() => {
              // Если были множественные — вернуться к выбору
              if (phoneResult?.companies && phoneResult.companies.length > 1) {
                setStep('select-company');
              } else {
                setStep('phone');
                setPhoneResult(null);
              }
              setSelectedCompany(null);
              setError('');
            }}
          >
            Назад
          </Button>
        </div>
      </div>
    );
  }

  // Шаг 2b: Форма заявки (контакт не найден)
  if (step === 'not-found') {
    return (
      <div style={{ padding: '16px' }}>
        <Placeholder
          header="Контакт не найден"
          description="Мы не нашли вас в нашей базе. Оставьте заявку, и мы свяжемся с вами."
        />

        <Section header="Ваши данные">
          <Input
            header="ФИО *"
            placeholder="Иванов Иван Иванович"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
          <Input
            header="Телефон"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            type="tel"
          />
          <Input
            header="Email"
            placeholder="email@company.ru"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
          />
          <Input
            header="ИНН компании"
            placeholder="1234567890"
            value={inn}
            onChange={(e) => setInn(e.target.value)}
          />
        </Section>

        <Section header="Комментарий">
          <Textarea
            placeholder="Опишите, чем мы можем помочь..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </Section>

        {error && (
          <Text style={{ color: 'var(--tgui--destructive_text_color)', padding: '8px 16px', fontSize: '14px' }}>
            {error}
          </Text>
        )}

        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Button
            size="l"
            stretched
            onClick={handleRequestAccess}
            loading={isLoading}
            disabled={!fullName.trim()}
          >
            Отправить заявку
          </Button>
          <Button
            size="l"
            stretched
            mode="outline"
            onClick={() => {
              setStep('phone');
              setError('');
            }}
          >
            Назад
          </Button>
        </div>

        <div style={{ padding: '0 16px' }}>
          <ContactManagerLink showContacts />
        </div>
      </div>
    );
  }

  // Шаг 3: Заявка отправлена
  if (step === 'request-sent') {
    return (
      <div style={{ padding: '16px' }}>
        <Placeholder
          header="Заявка отправлена!"
          description="Мы получили вашу заявку и свяжемся с вами в ближайшее время. Спасибо!"
        />
        <div style={{ padding: '0 16px' }}>
          <ContactManagerLink showContacts />
        </div>
      </div>
    );
  }

  return null;
}
